import { assert, definedMap, hasOwnProperty } from "@glideapps/ts-necessities";
import toPairs from "lodash/toPairs";
import type { ChangeObservable } from "../common/change-obserable";
import { Watchable } from "../common/change-obserable";
import type { PrimitiveValue } from "../common/components/computation-types";
import type { ValuesAndDatas } from "../common/components/data";
import type { AppUserData, DataRowStore, TableDataEntries } from "../common/components/datastore/data-row-store";
import type { AppEnvironment, AppOther, ValueProducer } from "../common/components/types";
import type { GroundValue, Row, Table } from "../common/computation-model/data";
import { getRowColumn } from "../common/computation-model/data";
import type { Handler, IncomingSlot, Namespace } from "../common/computation-model/types";
import type { DocumentData, RowIndex } from "../common/Database";
import { logError } from "../common/debug-print";
import type { TableGlideType } from "../common/description";
import { isBaseRowIndex } from "../common/firebase-function-types";
import { convertGlideDateTime } from "../common/glide-date-time";
import { rowIndexColumnName } from "../common/google-sheets";
import { makeRowID } from "../common/make-row-id";
import { truthify } from "../support";
import { isNewRowOwnedByUser } from "./is-row-owned";

export function findRowInTable(table: Table, rowIndex: RowIndex, rowIDColumnName: string | undefined): Row | undefined {
    let keyColumnName: string;
    let keyColumnValue: unknown;
    if (isBaseRowIndex(rowIndex)) {
        keyColumnName = rowIndexColumnName;
        keyColumnValue = rowIndex;
    } else {
        keyColumnName = rowIndex.keyColumnName;
        keyColumnValue = rowIndex.keyColumnValue;
    }
    assert(keyColumnValue !== undefined);

    if (keyColumnName === rowIDColumnName) {
        if (typeof keyColumnValue !== "string") return undefined;
        return table.get(keyColumnValue);
    }

    for (const row of table.values()) {
        const column = getRowColumn(row, keyColumnName);
        if (column === keyColumnValue) {
            return row;
        }
    }
    return undefined;
}

export class ComputationModelDataRowStore implements DataRowStore, Handler {
    private _ns: Namespace | undefined;
    private readonly _allRowsByDocumentID = new Map<string, Row>();
    // This does not contain the same set of rows as `_allRowsByDocumentID`.
    // Invisible rows are only added here, for example, but rows not owned by
    // the current user won't be added here.
    private readonly _tableData = new Map<string, Row>();
    private readonly _addedRows = new Map<string, Row>();
    private readonly _firstRowObservable = new Watchable<Row | undefined>(undefined);
    public readonly symbolicRepresentation = "data-row-store";

    constructor(
        private readonly _filterByEmail: boolean,
        private readonly _getTable: () => TableGlideType | undefined,
        private readonly _getAppUserData: () => AppUserData | undefined,
        private readonly _fetchData: () => void
    ) {}

    public getSlots(): readonly IncomingSlot[] {
        return [];
    }

    public recompute(): GroundValue {
        // This will initiate fetching the data for this table, if that hasn't
        // happened already.
        this._fetchData();
        return this._tableData;
    }

    public setDirty(): void {
        return;
    }

    public get isDirty(): boolean {
        return false;
    }

    public get firstRowObservable(): ChangeObservable<Row | undefined> {
        return this._firstRowObservable;
    }

    private setFirstRowIfNecessary(): void {
        if (this.firstRowObservable.current !== undefined) return;

        let row: Row | undefined;
        for (const r of this._tableData.values()) {
            if (r.$isVisible) {
                row = r;
                break;
            }
        }
        if (row === undefined) return;
        this._firstRowObservable.current = row;
    }

    private setRowInTableData(row: Row): void {
        this._tableData.set(row.$rowID, row);
        this.setFirstRowIfNecessary();
    }

    private isRowIncluded(row: Row, appUserData: AppUserData | undefined): boolean {
        if (!this._filterByEmail) return true;
        return isNewRowOwnedByUser(row, appUserData, this._getTable());
    }

    private pushDirtForRow(rowID: string, columns: ReadonlySet<string> | true = true): void {
        if (columns !== true && columns.size === 0) return;

        this._ns?.pushDirt(this, { kind: "row", rowID, columns });
    }

    private findRow(rowIndex: RowIndex): Row | undefined {
        const rowIDColumn = this._getTable()?.rowIDColumn;
        return findRowInTable(this._tableData, rowIndex, rowIDColumn);
    }

    // This duplicates a lot of what's in ##updateRowData.
    private updateRowData(row: Row, updates: DocumentData, onlyUserSpecific: boolean): void {
        const dirtyColumns = new Set<string>();
        for (const { name, isUserSpecific, formula } of this._getTable()?.columns ?? []) {
            if (onlyUserSpecific && !truthify(isUserSpecific)) continue;
            if (formula !== undefined) continue;

            const value: PrimitiveValue = convertGlideDateTime(updates[name]);
            const existingValue = getRowColumn(row, name);

            if (value === existingValue) continue;
            if (value === undefined && !(name in updates)) continue;

            row[name] = value;
            dirtyColumns.add(name);
        }

        this.pushDirtForRow(row.$rowID, dirtyColumns);
    }

    public addInvisibleRow(row: Row): void {
        assert(!row.$isVisible);
        this.setRowInTableData(row);
        this.pushDirtForRow(row.$rowID);
    }

    public deleteInvisibleRow(rowID: string): void {
        const row = this._tableData.get(rowID);
        if (row === undefined) return;
        assert(!row.$isVisible);
        this._tableData.delete(rowID);
        this.pushDirtForRow(rowID);
    }

    public setAppDataForSortedEntries(sortedEntries: TableDataEntries): number | undefined {
        const rowIDsNotSeen = new Set(this._tableData.keys());
        const appUserData = this._getAppUserData();

        let numRowsAdded = 0;
        for (const [documentID] of sortedEntries) {
            const row = this._allRowsByDocumentID.get(documentID);
            // This can happen if the row is deleted, so we don't have its
            // document, but the caller doesn't care and still gives it to us.
            if (row === undefined) continue;

            if (!this.isRowIncluded(row, appUserData)) continue;

            numRowsAdded++;

            const rowID = row.$rowID;
            rowIDsNotSeen.delete(rowID);

            if (!this._tableData.has(rowID)) {
                this.setRowInTableData(row);
                this.pushDirtForRow(rowID);
            }

            this._addedRows.delete(rowID);
        }

        for (const rowID of this._addedRows.keys()) {
            rowIDsNotSeen.delete(rowID);
            numRowsAdded++;
        }

        for (const rowID of rowIDsNotSeen) {
            const row = this._tableData.get(rowID);
            // We don't delete invisible rows, as they're not really part of
            // the app data.
            if (row?.$isVisible !== true) continue;

            this._tableData.delete(rowID);
            this.pushDirtForRow(rowID);
        }

        return numRowsAdded;
    }

    private getExistingOrAddedRow(documentID: string, isAdded: boolean, rowID: string | undefined): Row | undefined {
        let row = this._allRowsByDocumentID.get(documentID);
        if (row === undefined) {
            if (!isAdded) return undefined;

            if (rowID !== undefined) {
                row = this._addedRows.get(rowID);
                if (row !== undefined) {
                    this._addedRows.delete(rowID);
                }
            } else {
                for (const r of this._addedRows.values()) {
                    row = r;
                    this._addedRows.delete(row.$rowID);
                    break;
                }
            }

            if (row !== undefined) {
                this._allRowsByDocumentID.set(documentID, row);
            }
        }
        return row;
    }

    public updateRow(
        documentID: string,
        isAdded: boolean,
        mayAddRow: boolean,
        updates: DocumentData,
        userDataUpdates: DocumentData | undefined,
        fullData: DocumentData,
        fullUserData: DocumentData | undefined
    ): { reprocessUserSpecificData: boolean; didAddRow: boolean } | undefined {
        const rowIDColumn = this._getTable()?.rowIDColumn;

        let row = this.getExistingOrAddedRow(
            documentID,
            isAdded,
            definedMap(rowIDColumn, c => fullData[c])
        );

        let reprocessUserSpecificData = false;
        let didAddRow = false;

        if (row !== undefined) {
            if (rowIDColumn !== undefined && hasOwnProperty(updates, rowIDColumn)) {
                // The row ID for a row has changed, so we will reprocess all
                // user-specific data, since we keep it by row index.
                reprocessUserSpecificData = true;
            }

            this.updateRowData(row, { ...updates, ...userDataUpdates }, false);
        } else if (mayAddRow) {
            if (!isAdded) {
                logError("Modified row doesn't exist yet", documentID);
            }

            didAddRow = true;

            // If we don't have a row ID, the document ID is the best we can
            // do.
            row = { $rowID: definedMap(rowIDColumn, c => fullData[c]) ?? documentID, $isVisible: true };

            this._allRowsByDocumentID.set(documentID, row);

            this.updateRowData(row, { ...fullData, ...fullUserData }, false);
        }

        return { reprocessUserSpecificData, didAddRow };
    }

    public updateRowIfPresent(documentID: string, updates: DocumentData): void {
        const row = this._allRowsByDocumentID.get(documentID);
        if (row === undefined) return;

        this.updateRowData(row, updates, true);
    }

    public removeComputedColumns(_columnsToDelete: readonly string[], _alwaysSetDirty: boolean): void {
        // Not our responsibility
        return;
    }

    // Takes possession of the row
    public addRow(row: Row): void {
        if (!this.isRowIncluded(row, this._getAppUserData())) return;

        this._addedRows.set(row.$rowID, row);

        this.setRowInTableData(row);
        this.pushDirtForRow(row.$rowID);
    }

    public addRowWithValues(
        values: ValuesAndDatas<AppOther, AppEnvironment>,
        rowID: string | undefined
    ): ValueProducer | undefined {
        if (rowID === undefined) {
            rowID = makeRowID();
        }

        const row: Row = { ...values.values, $rowID: rowID, $isVisible: true };
        this.addRow(row);

        return undefined;
    }

    public setColumnsInRow(rowIndex: RowIndex, values: ValuesAndDatas<AppOther, AppEnvironment>): void {
        const row = this.findRow(rowIndex);
        if (row === undefined) return;

        const dirtyColumns = new Set<string>();

        for (const [columnName, columnValue] of toPairs(values.values)) {
            // FIXME: Are we not converting GlideDateTimes here?
            const existingValue = getRowColumn(row, columnName);
            if (columnValue === existingValue) continue;

            row[columnName] = columnValue;
            dirtyColumns.add(columnName);
        }

        this.pushDirtForRow(row.$rowID, dirtyColumns);
    }

    public updateRowWithPartialData(rowIndex: RowIndex, updates: DocumentData): void {
        const row = this.findRow(rowIndex);
        if (row === undefined) return;

        this.updateRowData(row, updates, false);
    }

    public deleteRowAtIndex(rowIndex: RowIndex): void {
        const row = this.findRow(rowIndex);
        if (row === undefined) return;

        this._tableData.delete(row.$rowID);
        this.pushDirtForRow(row.$rowID);
    }

    public deleteDataRow(documentID: string): void {
        const row = this._allRowsByDocumentID.get(documentID);
        if (row === undefined) return;

        this._allRowsByDocumentID.delete(documentID);
    }

    public clear(): void {
        this._allRowsByDocumentID.clear();
    }

    public connect(ns: Namespace): void {
        this._ns = ns;
    }
}
